import React, { useEffect, useState, useRef } from 'react';
import { Popper } from '@mui/material';
import { ErrMessage } from '../messages';
import { Images } from 'utils';

const activeStyles = {
    background: '#0C66E4',
    color: 'white',
};

/** Always lists every minute: the org-wide minute step (`useTimeInputMinuteStep`) applies to `TimeInput` only. */
export const TimeInputFull = ({ errorFalse, typeError, label, onChange, name, defaultValue, customClass }) => {
    const [time, setTime] = useState({ hour: '', minute: '', second: '', type: '' });
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const secondRef = useRef(null);
    const typeRef = useRef(null);

    const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
    const seconds = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
    const ampm = ['AM', 'PM'];
    const didInit = useRef(false);

    useEffect(() => {
        if (!didInit.current && defaultValue) {
            didInit.current = true;
            let h = '',
                m = '',
                s = '00',
                period = '';

            const match12 = defaultValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s?(AM|PM)$/i);
            const match24 = defaultValue.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);

            if (match12) {
                [, h, m, s, period] = match12;
                h = String(parseInt(h, 10)).padStart(2, '0');
                m = String(parseInt(m, 10)).padStart(2, '0');
                s = s ? String(parseInt(s, 10)).padStart(2, '0') : '00';
                period = period.toUpperCase();
            } else if (match24) {
                [, h, m, s] = match24;
                const hourNum = parseInt(h, 10);
                period = hourNum >= 12 ? 'PM' : 'AM';
                h = String(hourNum % 12 || 12).padStart(2, '0');
                s = s ? String(parseInt(s, 10)).padStart(2, '0') : '00';
            }
            setTime({ hour: h, minute: m, second: s, type: period });
        }
    }, [defaultValue]);

    const updateTime = (updated) => {
        setTime(updated);
        if (updated.hour && updated.minute && updated.second && updated.type) {
            let h24 = parseInt(updated.hour, 10) % 12;
            if (updated.type === 'PM') h24 += 12;
            const formatted = `${String(h24).padStart(2, '0')}:${updated.minute}:${updated.second}`;
            onChange({ target: { name, value: formatted } });
        }
    };

    const handleChange = (field, value) => {
        if (field === 'type') {
            value = value.trim().toUpperCase();
            if (value === 'A') value = 'AM';
            if (value === 'P') value = 'PM';
            if (value !== 'AM' && value !== 'PM') return;
            updateTime({ ...time, type: value });
            return;
        }

        let cleaned = value.replace(/\D/g, '');
        if (field === 'hour' && (parseInt(cleaned, 10) > 12 || cleaned.length > 2)) return;
        if (field === 'minute' && (parseInt(cleaned, 10) > 59 || cleaned.length > 2)) return;
        if (field === 'second' && (parseInt(cleaned, 10) > 59 || cleaned.length > 2)) return;

        const updated = { ...time, [field]: cleaned };
        updateTime(updated);

        if (field === 'hour' && cleaned.length === 2) minuteRef.current?.focus();
        if (field === 'minute' && cleaned.length === 2) secondRef.current?.focus();
        if (field === 'second' && cleaned.length === 2) typeRef.current?.focus();
        if (field === 'minute' && cleaned.length === 0) hourRef.current?.focus();
        if (field === 'second' && cleaned.length === 0) minuteRef.current?.focus();
    };

    const handleKeyDown = (inputName, e) => {
        if (e.key === 'Backspace') {
            if (inputName === 'minute' && time.minute.length === 0) hourRef.current?.focus();
            if (inputName === 'second' && time.second.length === 0) minuteRef.current?.focus();
            if (inputName === 'type') typeRef.current?.focus();
        }
    };

    const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);
    const handleCloseAnchor = () => setAnchorEl(null);

    return (
        <div className="time-input-box">
            <p style={typeError ? { color: '#f44336' } : {}} className="time-input-title">
                {label}
            </p>

            <div className={`time-input ${typeError ? 'time-input-error' : ''} ${customClass}`}>
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                    <input
                        ref={hourRef}
                        type="text"
                        placeholder="hh"
                        value={time.hour}
                        onChange={(e) => handleChange('hour', e.target.value)}
                        maxLength={2}
                        style={{ width: 20 }}
                    />
                    <span style={{ marginBottom: 2 }}>:</span>
                    <input
                        ref={minuteRef}
                        type="text"
                        placeholder="mm"
                        value={time.minute}
                        onChange={(e) => handleChange('minute', e.target.value)}
                        onKeyDown={(e) => handleKeyDown('minute', e)}
                        maxLength={2}
                        style={{ width: 28 }}
                    />
                    <span style={{ marginBottom: 2 }}>:</span>
                    <input
                        ref={secondRef}
                        type="text"
                        placeholder="ss"
                        value={time.second}
                        onChange={(e) => handleChange('second', e.target.value)}
                        onKeyDown={(e) => handleKeyDown('second', e)}
                        maxLength={2}
                        style={{ width: 28 }}
                    />
                    <input
                        ref={typeRef}
                        type="text"
                        placeholder="AM/PM"
                        value={time.type}
                        maxLength={2}
                        onChange={(e) => handleChange('type', e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Backspace') {
                                setTime({ ...time, type: '' });
                                secondRef.current?.focus();
                                e.preventDefault();
                            } else {
                                handleChange('type', e.key);
                            }
                        }}
                        style={{ width: 44, textTransform: 'uppercase', fontSize: 12 }}
                    />
                </div>

                <div className="flex-align-center">
                    {defaultValue && (
                        <button
                            style={{ marginTop: 4 }}
                            className="remove-calendar-day"
                            type="button"
                            onClick={() => {
                                setTime({ hour: '', minute: '', second: '', type: '' });
                                onChange({ target: { name, value: null } });
                            }}
                        >
                            <img style={{ margin: 0 }} src={Images.removeBlack} alt="remove" />
                        </button>
                    )}
                    <div onClick={handleClick} style={{ cursor: 'pointer' }} className="time-icon">
                        <img src={Images.accessTime} alt="time icon" />
                    </div>
                </div>
            </div>

            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className="custom-select-time-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 2600 }}
            >
                <div className="hour-minute-box">
                    <div>
                        {hours.map((item) => (
                            <button key={item} onClick={() => handleChange('hour', item)} style={item === time.hour ? activeStyles : {}}>
                                {item}
                            </button>
                        ))}
                    </div>
                    <div>
                        {minutes.map((item) => (
                            <button
                                key={item}
                                onClick={() => handleChange('minute', item)}
                                style={item === time.minute ? activeStyles : {}}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                    <div>
                        {seconds.map((item) => (
                            <button
                                key={item}
                                onClick={() => handleChange('second', item)}
                                style={item === time.second ? activeStyles : {}}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                    <div>
                        {ampm.map((item) => (
                            <button key={item} onClick={() => handleChange('type', item)} style={item === time.type ? activeStyles : {}}>
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            </Popper>

            {!errorFalse && <ErrMessage style={{ marginBottom: '12px' }} text={typeError} />}
            {open && <div className="beck-drop" onClick={handleCloseAnchor} />}
        </div>
    );
};
